import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEventJobConfig } from '@/api/media/live-event-jobs';
import { useCreateLinearJob } from '@/api/media/live-linear-jobs';
import { useToast } from '@vnetwork/ui/feedback';
import type {
  CreateLinearJobRequest,
  LinearConfig,
  LinearInputProtocol,
  LogoConfig,
} from '@/api/media/live-linear-jobs-types';
import type {
  HlsProtection,
  DashProtection,
  RecordingConfig,
  SnapshotConfig,
  EventJob,
} from '@/api/media/live-event-jobs-types';

// ─── Constants ───────────────────────────────────────────────────────────────

const NAME_REGEX = /^[a-zA-Z0-9_]{1,64}$/;

// Input protocols where MPEGTS custom config panel is needed
export const CUSTOM_CONFIG_PROTOCOLS: LinearInputProtocol[] = [
  'mpegts', 'mpegts_udp_multicast', 'srt', 'rtp',
];

// Protocols that force DASH recording mode
export const DASH_RECORDING_PROTOCOLS: LinearInputProtocol[] = ['srt', 'mpegts'];

// Protocols supporting DASH advanced time-shift
export const DASH_ADVANCED_SHIFT_PROTOCOLS: LinearInputProtocol[] = ['mpegts', 'rtmp', 'srt'];

export function resolveValidOutputs(
  inputProtocol: LinearInputProtocol,
  outputs: CreateLinearJobRequest['outputs'],
) {
  if (inputProtocol === 'mp3rtp') return outputs.filter((o) => o === 'hls');
  if (inputProtocol === 'mp3Rtmp') return outputs.filter((o) => o === 'hls' || o === 'dash');
  if (inputProtocol === 'mpegts' || inputProtocol === 'srt')
    return outputs.filter((o) => o !== 'rtmp' && o !== 'http-flv');
  return outputs;
}

export function canUseDashAdvancedTimeShift(
  inputProtocol: LinearInputProtocol,
  outputs: CreateLinearJobRequest['outputs'],
) {
  return DASH_ADVANCED_SHIFT_PROTOCOLS.includes(inputProtocol) && outputs.includes('dash');
}

// ─── Default state ────────────────────────────────────────────────────────────

const defaultLinearConfig: LinearConfig = {
  video_pids: [],
  scte35_pids: '',
  audio_tracks: [{ pid: '', language: 'eng', codec: 'aac', default: true }],
  subtitle_tracks: [{ pid: '', language: 'eng', codec: 'dvb_subtitle', page: '' }],
  multicast_ip: '',
  multicast_port: '',
  dash_order_bitrate: false,
  dash_advanced_time_shifted_features: false,
  ha_pairs: [],
  ingested_programs: [{ video_pid: '', audio_pid: '' }],
  dai_enabled: false,
};

const defaultForm: CreateLinearJobRequest = {
  name: '',
  app_name: '',
  stream_name: '',
  input_protocol: 'rtmp',
  outputs: [],
  presets: [],
  linear_config: defaultLinearConfig,
  timeshift_enabled: false,
  low_latency_enabled: false,
  gpu_enabled: false,
};

const defaultHls: HlsProtection = { mode: 'none' };
const defaultDash: DashProtection = { mode: 'no_drm' };
// Default recording storage seeds ftp (legacy default)
const defaultRecording: RecordingConfig = { enabled: false, mode: 'hls', storage: { type: 'ftp', storage_id: '', path: '' } };
const defaultSnapshot: SnapshotConfig = { enabled: false, type: 'jpg' };
const defaultLogo: LogoConfig = {
  enabled: false,
  file_path: '',
  width: '',
  width_unit: '%',
  height: '',
  height_unit: '%',
  horizontal_align: 'left',
  vertical_align: 'top',
  dx: '',
  dx_unit: 'px',
  dy: '',
  dy_unit: 'px',
  sizing_policy: '0',
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export type LinearJobFormErrors = Record<string, string>;

export function useLinearJobForm(serviceId: string) {
  const navigate = useNavigate();
  const { show } = useToast();

  const { data: config, isLoading: isConfigLoading } = useEventJobConfig(serviceId);

  const [form, setForm] = useState<CreateLinearJobRequest>(defaultForm);
  const [audioOnlyFormat, setAudioOnlyFormat] = useState<'aac' | 'mp3'>('aac');
  const [hlsProtection, setHlsProtection] = useState<HlsProtection>(defaultHls);
  const [dashProtection, setDashProtection] = useState<DashProtection>(defaultDash);
  const [recording, setRecording] = useState<RecordingConfig>(defaultRecording);
  const [snapshot, setSnapshot] = useState<SnapshotConfig>(defaultSnapshot);
  const [logo, setLogo] = useState<LogoConfig>(defaultLogo);
  const [errors, setErrors] = useState<LinearJobFormErrors>({});
  const [createdJob, setCreatedJob] = useState<EventJob | null>(null);

  const createMut = useCreateLinearJob(serviceId, {
    onSuccess: (job) => {
      show({ tone: 'success', title: 'Linear job created' });
      setCreatedJob(job);
    },
    onError: (e) => show({ tone: 'danger', title: e.message || 'Failed to create linear job' }),
  });

  const updateForm = (field: keyof CreateLinearJobRequest, value: unknown) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value } as CreateLinearJobRequest;
      // Clear HA pairs when leaving RTMP
      if (field === 'input_protocol' && value !== 'rtmp') {
        next.linear_config = { ...prev.linear_config, ha_pairs: [] };
      }
      // Recalculate dash_advanced_time_shifted_features eligibility
      if (field === 'input_protocol' || field === 'outputs') {
        const ip = field === 'input_protocol' ? (value as LinearInputProtocol) : prev.input_protocol;
        const outs = field === 'outputs' ? (value as CreateLinearJobRequest['outputs']) : prev.outputs;
        if (!canUseDashAdvancedTimeShift(ip, outs)) {
          next.linear_config = { ...next.linear_config, dash_advanced_time_shifted_features: false };
        }
      }
      return next;
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field as string];
      return next;
    });
  };

  const updateLinearConfig = (linearConfig: LinearConfig) => {
    setForm((prev) => ({ ...prev, linear_config: linearConfig }));
  };

  const clearError = (field: string) => {
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const validate = (): boolean => {
    const next: LinearJobFormErrors = {};

    if (!NAME_REGEX.test(form.app_name.trim()))
      next.app_name = 'Only letters, digits, and underscores — max 64 characters.';
    if (!NAME_REGEX.test(form.stream_name.trim()))
      next.stream_name = 'Only letters, digits, and underscores — max 64 characters.';

    const validOuts = resolveValidOutputs(form.input_protocol, form.outputs);
    if (form.outputs.length === 0) {
      next.outputs = 'Select at least one output.';
    } else if (validOuts.length !== form.outputs.length) {
      next.outputs = 'Selected outputs are not valid for this input protocol.';
    }
    if (form.presets.length === 0) next.presets = 'Select at least one preset.';
    if (logo.enabled && !logo.file_path?.trim()) next['logo.file_path'] = 'Logo file path is required.';

    const haPairs = form.linear_config.ha_pairs || [];
    if (form.input_protocol === 'rtmp' && haPairs.length > 0) {
      if (haPairs.length !== 2) {
        next['linear_config.ha_pairs'] = 'HA requires exactly two stream pairs.';
      } else if (haPairs.some((p) => !p.ingestion_domain.trim() || !p.origin_domain.trim())) {
        next['linear_config.ha_pairs'] = 'Both Ingestion and Origin Domains are required for both HA streams.';
      }
    }

    const showCustomConfig = CUSTOM_CONFIG_PROTOCOLS.includes(form.input_protocol);
    if (showCustomConfig) {
      const isRtp = form.input_protocol === 'rtp';
      const isMpegtsOrSrt = ['mpegts', 'mpegts_udp_multicast', 'srt'].includes(form.input_protocol);

      if (!isRtp) {
        if (!form.linear_config.video_pids?.length || !form.linear_config.video_pids[0].trim())
          next['linear_config.video_pids'] = 'Video PID is required for this input protocol.';
        if (
          form.input_protocol === 'mpegts' &&
          form.linear_config.dai_enabled &&
          !form.linear_config.scte35_pids?.trim()
        ) {
          next['linear_config.scte35_pids'] = 'SCTE-35 PIDs required when DAI is enabled.';
        }
      }

      if (isMpegtsOrSrt) {
        const audioTracks = form.linear_config.audio_tracks || [];
        if (!audioTracks.length) {
          next['linear_config.audio_tracks'] = 'At least one audio track is required.';
        } else if (audioTracks.some((t) => !t.pid.trim())) {
          next['linear_config.audio_tracks'] = 'All audio tracks must have a PID.';
        } else {
          const pids = new Set(audioTracks.map((t) => t.pid.trim()));
          const langs = new Set(audioTracks.map((t) => t.language));
          if (pids.size !== audioTracks.length)
            next['linear_config.audio_tracks'] = 'Audio tracks must have unique PIDs.';
          else if (langs.size !== audioTracks.length)
            next['linear_config.audio_tracks'] = 'Audio tracks must have unique languages.';
        }

        const subtitleTracks = form.linear_config.subtitle_tracks || [];
        if (subtitleTracks.length) {
          if (subtitleTracks.some((t) => !t.pid.trim())) {
            next['linear_config.subtitle_tracks'] = 'All subtitle tracks must have a PID.';
          } else {
            const pids = new Set(subtitleTracks.map((t) => t.pid.trim()));
            const langs = new Set(subtitleTracks.map((t) => t.language));
            if (pids.size !== subtitleTracks.length)
              next['linear_config.subtitle_tracks'] = 'Subtitle tracks must have unique PIDs.';
            else if (langs.size !== subtitleTracks.length)
              next['linear_config.subtitle_tracks'] = 'Subtitle tracks must have unique languages.';
            if (
              !next['linear_config.subtitle_tracks'] &&
              subtitleTracks.some((t) => t.codec === 'teletext' && !t.page?.trim())
            ) {
              next['linear_config.subtitle_tracks'] = 'Teletext subtitle tracks require a Page.';
            }
          }
        }
      }

      const isMulticast = form.input_protocol === 'mpegts_udp_multicast' || isRtp;
      if (isMulticast) {
        if (!form.linear_config.multicast_ip) next['linear_config.multicast_ip'] = 'Multicast IP is required.';
        if (!form.linear_config.multicast_port) next['linear_config.multicast_port'] = 'Multicast Port is required.';
      }

      if (isRtp) {
        const progs = form.linear_config.ingested_programs || [];
        if (!progs.length) {
          next['linear_config.ingested_programs'] = 'At least one ingested program is required for RTP.';
        } else if (progs.some((p) => !p.video_pid.trim() || !p.audio_pid.trim())) {
          next['linear_config.ingested_programs'] = 'All ingested programs must have Video PID and Audio PID.';
        }
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const buildPayload = (): CreateLinearJobRequest => {
    const payload: CreateLinearJobRequest = {
      ...form,
      app_name: form.app_name.trim(),
      stream_name: form.stream_name.trim(),
      name: form.stream_name.trim(),
      outputs: resolveValidOutputs(form.input_protocol, form.outputs),
    };

    const showCustomConfig = CUSTOM_CONFIG_PROTOCOLS.includes(payload.input_protocol);
    if (!showCustomConfig) {
      const clean: LinearConfig = {};
      if (payload.input_protocol === 'rtmp' && (payload.linear_config.ha_pairs?.length ?? 0) > 0) {
        clean.ha_pairs = payload.linear_config.ha_pairs!.map((p) => ({
          ingestion_domain: p.ingestion_domain.trim(),
          origin_domain: p.origin_domain.trim(),
        }));
      }
      if (
        canUseDashAdvancedTimeShift(payload.input_protocol, payload.outputs) &&
        payload.linear_config.dash_advanced_time_shifted_features
      ) {
        clean.dash_advanced_time_shifted_features = true;
      }
      // dash_order_bitrate is only meaningful for custom-config protocols (mpegts/srt/rtp);
      // legacy never sends it for rtmp (non-custom clean object never includes it)
      payload.linear_config = clean;
    } else {
      const clean = { ...payload.linear_config };
      clean.video_pids = clean.video_pids?.filter((p) => p.trim() !== '');
      clean.audio_tracks = clean.audio_tracks?.filter((t) => t.pid.trim() !== '');
      clean.subtitle_tracks = clean.subtitle_tracks?.filter((t) => t.pid.trim() !== '');
      if (payload.input_protocol === 'mpegts') {
        if (!clean.dai_enabled) delete clean.scte35_pids;
      } else {
        delete clean.dai_enabled;
        delete clean.scte35_pids;
      }
      if (payload.input_protocol === 'rtp') {
        clean.ingested_programs = clean.ingested_programs?.filter(
          (p) => p.video_pid.trim() !== '' || p.audio_pid.trim() !== '',
        );
      } else {
        delete clean.ingested_programs;
      }
      delete clean.ha_pairs;
      if (
        !canUseDashAdvancedTimeShift(payload.input_protocol, payload.outputs) ||
        !clean.dash_advanced_time_shifted_features
      ) {
        delete clean.dash_advanced_time_shifted_features;
      }
      payload.linear_config = clean;
    }

    if (form.presets.includes('audio_only')) payload.audio_only = { format: audioOnlyFormat };
    if (form.outputs.includes('hls')) payload.hls_protection = hlsProtection;
    if (form.outputs.includes('dash')) payload.dash_protection = dashProtection;

    if (recording.enabled) {
      payload.recording = { ...recording };
      if (DASH_RECORDING_PROTOCOLS.includes(form.input_protocol)) {
        payload.recording.mode = 'dash';
      }
    }
    if (snapshot.enabled) payload.snapshot = snapshot;

    if (logo.enabled) {
      payload.logo = {
        ...logo,
        file_path: logo.file_path?.trim(),
        width: logo.width?.trim(),
        height: logo.height?.trim(),
        dx: logo.dx?.trim(),
        dy: logo.dy?.trim(),
      };
    }

    return payload;
  };

  const handleSubmit = () => {
    if (!validate()) {
      show({ tone: 'warning', title: 'Please fill all required fields correctly.' });
      return;
    }
    createMut.mutate(buildPayload());
  };

  return {
    navigate,
    serviceId,
    config,
    isConfigLoading,
    form,
    updateForm,
    updateLinearConfig,
    audioOnlyFormat,
    setAudioOnlyFormat,
    hlsProtection,
    setHlsProtection,
    dashProtection,
    setDashProtection,
    recording,
    setRecording,
    snapshot,
    setSnapshot,
    logo,
    setLogo,
    errors,
    clearError,
    createdJob,
    isSubmitting: createMut.isPending,
    handleSubmit,
  };
}
